import { useState, useEffect, useRef, useCallback } from 'react'
import './AssistanceWizard.css'

// Assistance always runs the full pipeline; stage selection is display-only.
const STEP_LABELS = ['Upload', 'Select Flows', 'Review & Run']

// Detect whether the Full Pipeline template is on the canvas.
// The template contains 'pipeline-' prefixed node types — if any are present,
// the template has been added (definition lives in nodeRegistry.js: 'template-full-pipeline').
function hasFullPipelineTemplate(nodes) {
  return Array.isArray(nodes) && nodes.some(n => n.data?.nodeType?.startsWith('pipeline-'))
}

const FLOWS = [
  {
    id: 'analyze',
    label: 'Analyze',
    description: 'Parse source code, map structure, identify programs and copybooks.',
  },
  {
    id: 'build',
    label: 'Build',
    description: 'Refactor and compile Java output from the analyzed source.',
  },
  {
    id: 'business',
    label: 'Business',
    description: 'Generate services and business logic from the refactored output.',
  },
]

export default function AssistanceWizard({ project, executing, nodes, onInsertTemplate, onRun, onClose }) {
  const [step, setStep] = useState(0)
  const [zipFile, setZipFile] = useState(null)
  const [zipError, setZipError] = useState(null)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const fileInputRef = useRef(null)

  // Dirty-state: only if user has staged a zip file
  const hasPendingState = zipFile !== null

  // Template guard — Assistance requires the Full Pipeline template on the canvas
  const hasTemplate = hasFullPipelineTemplate(nodes)

  const handleCloseRequest = useCallback(() => {
    if (hasPendingState) {
      setShowConfirmClose(true)
    } else {
      onClose()
    }
  }, [hasPendingState, onClose])

  // Esc closes with dirty-state guard
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleCloseRequest() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleCloseRequest])

  const handleZipChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setZipError('Only .zip files are accepted.')
      setZipFile(null)
      e.target.value = ''
      return
    }
    setZipError(null)
    setZipFile(file)
    console.log('[ASSIST_WIZ] zip selected:', file.name, '| size:', file.size, 'bytes')
  }

  const handleRun = () => {
    if (!onRun) return
    // Passes the staged zip to handleAssistanceRun() in DesignerView.
    // That function stages the file into the upload node, fires executeWorkflow()
    // (same path as the toolbar Run button), logs a message, and closes this wizard.
    // Stage selection here is display-only — full pipeline always runs.
    onRun(zipFile)
  }

  // Allow advancing only when the template is present; zip is validated at run time.
  const canAdvance = hasTemplate && step < STEP_LABELS.length - 1

  return (
    <div
      className="aw-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Assistance Wizard"
      onClick={handleCloseRequest}
    >
      <div className="aw-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="aw-header">
          <span className="aw-title">Assistance</span>
          <button className="aw-close" onClick={handleCloseRequest} aria-label="Close wizard">
            &times;
          </button>
        </div>

        {/* Stepper */}
        <div className="aw-stepper" role="list">
          {STEP_LABELS.map((label, i) => (
            <div
              key={label}
              className={`aw-step-indicator${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}
              role="listitem"
              aria-current={i === step ? 'step' : undefined}
            >
              <span className="aw-step-num" aria-hidden="true">
                {i < step ? '\u2713' : i + 1}
              </span>
              <span className="aw-step-label">{label}</span>
              {i < STEP_LABELS.length - 1 && (
                <span className="aw-step-connector" aria-hidden="true" />
              )}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="aw-body">
          {/* Template guard banner — shown on all steps when Full Pipeline is not on canvas */}
          {!hasTemplate && (
            <div className="aw-template-banner" role="alert">
              <span className="aw-template-banner-text">
                Assistance requires the <strong>Full Pipeline</strong> template on the canvas.
              </span>
              <button
                className="aw-btn aw-btn-insert-template"
                onClick={onInsertTemplate}
              >
                Insert Template
              </button>
            </div>
          )}
          {step === 0 && (
            <StepUpload
              zipFile={zipFile}
              zipError={zipError}
              fileInputRef={fileInputRef}
              onChange={handleZipChange}
            />
          )}
          {step === 1 && (
            <StepSelectFlows flows={FLOWS} />
          )}
          {step === 2 && (
            <StepReview
              project={project}
              zipFile={zipFile}
              flows={FLOWS}
              executing={executing}
              hasTemplate={hasTemplate}
              onRun={handleRun}
              hasRunner={!!onRun}
            />
          )}
        </div>

        {/* Footer nav */}
        <div className="aw-footer">
          {step > 0 && (
            <button className="aw-btn aw-btn-secondary" onClick={() => setStep(s => s - 1)}>
              Back
            </button>
          )}
          <span className="aw-footer-spacer" />
          {step < STEP_LABELS.length - 1 && (
            <button
              className="aw-btn aw-btn-primary"
              onClick={() => setStep(s => s + 1)}
              disabled={!canAdvance}
            >
              Next
            </button>
          )}
        </div>
      </div>

      {/* Dirty-close confirmation */}
      {showConfirmClose && (
        <div className="aw-confirm-overlay" onClick={e => e.stopPropagation()}>
          <div className="aw-confirm">
            <p>You have a file staged. Close anyway?</p>
            <div className="aw-confirm-actions">
              <button className="aw-btn aw-btn-secondary" onClick={() => setShowConfirmClose(false)}>
                Stay
              </button>
              <button className="aw-btn aw-btn-danger" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Step sub-components ───────────────────────────────────────────────────────

function StepUpload({ zipFile, zipError, fileInputRef, onChange }) {
  return (
    <div className="aw-step-body">
      <p className="aw-step-desc">
        Select the COBOL source ZIP to process. The file will be sent through the
        Ingest node when you run the workflow.
      </p>
      <div
        className="aw-dropzone"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
        tabIndex={0}
        role="button"
        aria-label="Select ZIP file"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          onChange={onChange}
          className="aw-hidden-input"
          aria-describedby={zipError ? 'aw-zip-error' : undefined}
        />
        {zipFile ? (
          <div className="aw-file-selected">
            <span className="aw-file-name">{zipFile.name}</span>
            <span className="aw-file-size">{(zipFile.size / 1024).toFixed(1)} KB</span>
          </div>
        ) : (
          <span className="aw-dropzone-prompt">Click to select a .zip file</span>
        )}
      </div>
      {zipError && (
        <p id="aw-zip-error" className="aw-error" role="alert">{zipError}</p>
      )}
      {!zipFile && !zipError && (
        <p className="aw-note">
          You can skip this step if your workflow already has a source configured.
        </p>
      )}
    </div>
  )
}

// Stage selection is display-only — all stages are always ON.
// Checkboxes are disabled so users can see what will run without being able to change it.
function StepSelectFlows({ flows }) {
  return (
    <div className="aw-step-body">
      <p className="aw-step-desc">
        Assistance runs the full pipeline — all stages are included automatically.
      </p>
      <div className="aw-flows" role="group" aria-label="Pipeline stages">
        {flows.map(flow => (
          <label
            key={flow.id}
            className="aw-flow-card selected aw-flow-card-readonly"
          >
            <input
              type="checkbox"
              checked={true}
              disabled={true}
              readOnly
              className="aw-flow-checkbox"
              aria-label={`${flow.label} — always included`}
            />
            <div className="aw-flow-info">
              <span className="aw-flow-label">{flow.label}</span>
              <span className="aw-flow-desc">{flow.description}</span>
            </div>
          </label>
        ))}
      </div>
      <p className="aw-note">
        Assistance runs the full pipeline (Analyze + Build + Business).
      </p>
    </div>
  )
}

function StepReview({ project, zipFile, flows, executing, hasTemplate, onRun, hasRunner }) {
  const stageLabel = flows.map(f => f.label).join(', ')
  const noZip = !zipFile
  const runBlocked = !hasTemplate || executing || noZip

  const runTitle = !hasTemplate
    ? 'Go back and insert the Full Pipeline template first'
    : executing
      ? 'A workflow is already running'
      : noZip
        ? 'Go back and select a ZIP file to run'
        : 'Start the full pipeline — this window will close automatically'

  return (
    <div className="aw-step-body">
      <p className="aw-step-desc">
        Review your selections before running.
      </p>

      <div className="aw-review-table" role="table" aria-label="Run summary">
        <div className="aw-review-row" role="row">
          <span className="aw-review-key" role="rowheader">Project</span>
          <span className="aw-review-val">{project?.name ?? '\u2014'}</span>
        </div>
        <div className="aw-review-row" role="row">
          <span className="aw-review-key" role="rowheader">ZIP file</span>
          <span className="aw-review-val">{zipFile ? zipFile.name : '(skipped)'}</span>
        </div>
        <div className="aw-review-row" role="row">
          <span className="aw-review-key" role="rowheader">Stages</span>
          <span className="aw-review-val">{stageLabel} (Full pipeline)</span>
        </div>
      </div>

      {!hasRunner && (
        <>
          <button className="aw-btn aw-btn-run" disabled>
            Run Workflow
          </button>
          <p className="aw-status-msg aw-status-not-wired" role="alert">
            Run isn&apos;t available here yet.
          </p>
        </>
      )}

      {hasRunner && (
        <>
          <button
            className="aw-btn aw-btn-run"
            onClick={onRun}
            disabled={runBlocked}
            title={runTitle}
          >
            {executing ? 'Running\u2026' : 'Run Workflow'}
          </button>
          {noZip && hasTemplate && (
            <p className="aw-note aw-note-warn" role="alert">
              Go back and select a ZIP file to start the pipeline.
            </p>
          )}
        </>
      )}
    </div>
  )
}
